import apiClient from "../lib/apiClients";

// ---- Types ----
export interface SearchResult {
  id: string;
  type: string;
  title: string;
  subtitle?: string;
  matched_fields?: string[];
  data?: Record<string, any>;
}

export interface SearchGroup {
  type: string;
  type_label: string;
  count: number;
  has_more: boolean;
  results: SearchResult[];
}

export interface GlobalSearchResponse {
  query: string;
  total: number;
  group_count: number;
  groups: SearchGroup[];
}

export interface Dossier {
  suspect?: Record<string, any> | null;
  asset_person?: Record<string, any> | null;
  totals?: Record<string, any>;
  cases: Array<{
    case: Record<string, any>;
    court_information: Array<Record<string, any>>;
    charges: Array<Record<string, any>>;
    court_hearings: Array<Record<string, any>>;
    pre_judgement_decisions: Array<Record<string, any>>;
    sentence_decisions: Array<Record<string, any>>;
    appeals: Array<{
      kind: string;
      court_appeal_number?: string;
      status?: string;
      decisions: Array<Record<string, any>>;
      hearings: Array<Record<string, any>>;
      [key: string]: any;
    }>;
    [key: string]: any;
  }>;
  bail: Array<Record<string, any>>;
  conversions?: Array<Record<string, any>>;
  asset_recovery?: {
    totals?: Record<string, any>;
    referrals: Array<Record<string, any>>;
  } | null;
  [key: string]: any;
}

export interface DossierResponse {
  count: number;
  dossiers: Dossier[];
}

export interface SearchOption {
  value: string;
  label: string;
  category?: string;
  [key: string]: any;
}

// ---- Endpoints ----

// Global search across all record types, grouped.
export const globalSearch = async (
  query: string,
  params?: { types?: string; category?: string; limit?: number },
) => {
  const search = new URLSearchParams({ q: query });
  if (params?.types) search.set("types", params.types);
  if (params?.category) search.set("category", params.category);
  if (params?.limit) search.set("limit", String(params.limit));

  const response = await apiClient.get<GlobalSearchResponse>(
    `search/?${search.toString()}`,
  );
  return response.data;
};

// Full crime record (dossier) of a person — by free-text query or by id.
export const fetchDossier = async (params: {
  q?: string;
  suspect_id?: string;
  asset_person_id?: string;
}) => {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  if (params.suspect_id) search.set("suspect_id", params.suspect_id);
  if (params.asset_person_id)
    search.set("asset_person_id", params.asset_person_id);

  const response = await apiClient.get<DossierResponse>(
    `search/dossier/?${search.toString()}`,
  );
  return response.data;
};

// Metadata: searchable types & categories (call once to build filter tabs).
export const fetchSearchOptions = async () => {
  const response = await apiClient.get<any>(`search/options/`);
  return response.data;
};
